import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo } from 'react';
import { SortingState } from '@tanstack/react-table';
import { useDateFilterSettings, useGetBriefsQuery, useProgramsQuery } from '@frontend/app/hooks';
import { DateRangeOptions, IDateRangeSettings } from '@frontend/app/components';
import { GetBriefsQuery_briefs_data } from '@frontend/app/queries/types/GetBriefsQuery';
import { BriefPaymentStatus } from '@services/budget-allocation/utils/getPaymentStatus';
import { BriefStatus } from '../types';

export type Brief = GetBriefsQuery_briefs_data;

export interface Filter {
  searchBy?: string;
  searchQuery?: string;
  projectIds?: number[];
  paymentStatus?: BriefPaymentStatus[];
  status?: BriefStatus[];
}

interface BriefsContextType {
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  data: Brief[];
  pageCount: number;
  isLoading: boolean;
  rowSelection: Record<string, boolean>;
  selectedRows: Brief[];
  dateRangeSettings: IDateRangeSettings;
  totalCount: number;
  columnVisibility: Record<string, boolean>;
  isFilterOpen: boolean;
  isEditBriefDrawerOpen: boolean;
  setPageIndex: (index: number) => void;
  setPageSize: (size: number) => void;
  setSorting: (sorting: SortingState) => void;
  setData: (data: Brief[]) => void;
  setPageCount: (count: number) => void;
  setIsLoading: (loading: boolean) => void;
  setRowSelection: (selection: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void;
  setTotalCount: (count: number) => void;
  setColumnVisibility: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setIsFilterOpen: (open: boolean) => void;
  setIsEditBriefDrawerOpen: (open: boolean) => void;
  setFilter: (filter: Filter) => void;
  filter: Filter;
  programs: Array<{
    id: number;
    title: string;
  }>;
  isProgramsLoading: boolean;
  fetchBriefs: () => Promise<void>;
}

const BriefsContext = createContext<BriefsContextType | undefined>(undefined);

export const useBriefs = () => {
  const context = useContext(BriefsContext);
  if (context === undefined) {
    throw new Error('useBriefs must be used within a BriefsProvider');
  }
  return context;
};

interface BriefsProviderProps {
  children: ReactNode;
}

const SORT_FIELD_MAPPER: Record<string, string> = {
  member: 'memberName',
  project: 'projectName',
  status: 'status',
  createdDate: 'createdDate',
  payment: 'payment',
  paymentStatus: 'paymentStatus',
};

// Create a stable reference for empty arrays/
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EMPTY_ARRAY: any[] = [];
const EMPTY_OBJECT = {};

export const BriefsProvider: React.FC<BriefsProviderProps> = ({ children }) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [data, setData] = useState<Brief[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isEditBriefDrawerOpen, setIsEditBriefDrawerOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>({});

  const { data: programsData, loading: isProgramsLoading } = useProgramsQuery();
  const { refetch: refetchBriefs } = useGetBriefsQuery({
    variables: {
      filters: {
        startDate: undefined,
        endDate: undefined,
        searchQuery: filter.searchQuery,
        page: pageIndex + 1,
        pageSize,
        sortBy: sorting[0]?.id ? SORT_FIELD_MAPPER[sorting[0].id] || sorting[0].id : undefined,
        sortDirection: sorting[0]?.desc ? 'desc' : 'asc',
      },
    },
    skip: true,
  });

  const dateRangeSettings = useDateFilterSettings(DateRangeOptions.LAST_6_MONTHS);

  // Memoize the row selection lookup for better performance
  const rowSelectionLookup = useMemo(() => {
    if (Object.keys(rowSelection).length === 0) return EMPTY_OBJECT;
    return rowSelection;
  }, [rowSelection]);

  // Optimize selectedRows calculation
  const selectedRows = useMemo(() => {
    if (Object.keys(rowSelectionLookup).length === 0) return EMPTY_ARRAY;
    return data.filter((row) => rowSelectionLookup[row.id]);
  }, [data, rowSelectionLookup]);

  const programs = useMemo(() => {
    if (!programsData?.programs) return EMPTY_ARRAY;
    return programsData.programs.map((program) => ({
      id: program.id,
      title: program.title,
    }));
  }, [programsData]);

  const fetchBriefs = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: briefsData } = await refetchBriefs({
        filters: {
          startDate: dateRangeSettings.dateRange?.startDate?.toISOString(),
          endDate: dateRangeSettings.dateRange?.endDate?.toISOString(),
          searchQuery: filter.searchQuery,
          searchBy: filter.searchBy,
          page: pageIndex + 1,
          pageSize,
          sortBy: sorting[0]?.id ? SORT_FIELD_MAPPER[sorting[0].id] || sorting[0].id : undefined,
          sortDirection: sorting[0]?.desc ? 'desc' : 'asc',
          briefStatus: filter.status,
          paymentStatus: filter.paymentStatus,
          projectIds: filter.projectIds,
        },
      });

      if (briefsData?.briefs) {
        setData(briefsData.briefs.data);
        setPageCount(briefsData.briefs.totalPages);
        setTotalCount(briefsData.briefs.total);
      }
    } catch (error) {
      console.error('Error fetching briefs:', error);
      setData(EMPTY_ARRAY);
    } finally {
      setIsLoading(false);
    }
  }, [
    refetchBriefs,
    dateRangeSettings.dateRange?.startDate,
    dateRangeSettings.dateRange?.endDate,
    filter,
    pageIndex,
    pageSize,
    sorting,
  ]);

  // Reset page index when filter changes
  useEffect(() => {
    setPageIndex(0);
  }, [filter, dateRangeSettings.dateRange?.startDate, dateRangeSettings.dateRange?.endDate]);

  // Fetch data when necessary
  useEffect(() => {
    fetchBriefs();
  }, [fetchBriefs]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      pageIndex,
      pageSize,
      sorting,
      data,
      pageCount,
      isLoading,
      rowSelection,
      selectedRows,
      dateRangeSettings,
      totalCount,
      columnVisibility,
      isFilterOpen,
      isEditBriefDrawerOpen,
      setPageIndex,
      setPageSize,
      setSorting,
      setData,
      setPageCount,
      setIsLoading,
      setRowSelection,
      setTotalCount,
      setColumnVisibility,
      setIsFilterOpen,
      setIsEditBriefDrawerOpen,
      setFilter,
      filter,
      programs,
      isProgramsLoading,
      fetchBriefs,
    }),
    [
      pageIndex,
      pageSize,
      sorting,
      data,
      pageCount,
      isLoading,
      rowSelection,
      selectedRows,
      dateRangeSettings,
      totalCount,
      columnVisibility,
      isFilterOpen,
      isEditBriefDrawerOpen,
      filter,
      programs,
      isProgramsLoading,
      fetchBriefs,
    ]
  );

  return <BriefsContext.Provider value={contextValue}>{children}</BriefsContext.Provider>;
};
